import { createTheme, darken, getContrastRatio, lighten, alpha, Theme, colors, buttonClasses, inputBaseClasses, svgIconClasses } from "@mui/material"
import { DEFAULT_THEME } from "./nextTheme"
import { NextTheme } from "./themeTypes/next"

buttonClasses.sizeExtraLarge = "MuiButton-sizeExtralarge"

const DEFAULT_CONTRAST = 4.5 // 3 is a very low threshold (it's only used with a Large Icon/Text). Due to WCAG 2.1 Rule 1.4.3 a threshold of 4.5 is recommended, https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html
export const getContrastText = (background: string, nextTheme: NextTheme | Theme) => {
    const dark = nextTheme.colors?.dark ?? colors.grey[800]
    const light = nextTheme.colors?.light ?? colors.grey[100]
    const contrastThreshold = nextTheme.palette?.contrastThreshold || DEFAULT_CONTRAST
    const contrastText = getContrastRatio(background, dark) >= contrastThreshold ? dark : light
    return contrastText
}

export function createBaseTheme(nextTheme: NextTheme): Theme {
    const font = {
        ...DEFAULT_THEME.font,
        ...nextTheme.font,
    }

    if (!font.fontFamily.secondary) {
        font.fontFamily.secondary = "inherit"
    }
    if (!font.fontFamily.condensed) {
        font.fontFamily.condensed = font.fontFamily.secondary
    }

    // Todo: need a better solution, quick fix for Sass-Loader json string
    // https://www.npmjs.com/package/node-sass-json-importer#importing-strings
    // TODO: remove this, we must exclude the json from the sass deployment
    const fontFamily = font?.fontFamily?.primary
    const buttonFontFamily = nextTheme.overwrites?.components?.button?.primary?.fontFamily
    const tabFontFamily = nextTheme.overwrites?.components?.tabs?.font?.fontFamily
    const borderRadius = nextTheme.radius?.default || DEFAULT_THEME.radius.default
    // nextTheme.overwrites?.components?.partlist?.groupHeader?.monochrom = true

    return createTheme(
        {
            name: nextTheme.name,
            palette: {
                primary: {
                    /** generate Light, Dark and contrastText colors based on MainColor  */
                    main: nextTheme.colors?.primary || DEFAULT_THEME.colors?.primary,
                },
                secondary: {
                    main: nextTheme.colors?.secondary || DEFAULT_THEME.colors.secondary,
                },
                error: {
                    main: nextTheme.colors?.danger || DEFAULT_THEME.colors.danger,
                },
                warning: {
                    main: nextTheme.colors?.warning || DEFAULT_THEME.colors.warning,
                },
                info: {
                    main: nextTheme.colors?.info || DEFAULT_THEME.colors?.info,
                },
                success: {
                    main: nextTheme.colors?.success || DEFAULT_THEME.colors.success,
                },
                highlight: {
                    main: nextTheme.palette?.highlight.main || nextTheme.colors?.highlight || DEFAULT_THEME.colors.highlight,
                    contrastText:
                        nextTheme.palette?.highlight.contrastText ||
                        getContrastText(nextTheme.colors?.highlight || DEFAULT_THEME.colors.highlight, nextTheme),
                    light: lighten(nextTheme.colors?.highlight || DEFAULT_THEME.colors.highlight, 0.68), // on default like #fbe3b2;
                    dark: darken(nextTheme.colors?.highlight || DEFAULT_THEME.colors.highlight, 0.4),
                },
                background: {
                    default: nextTheme.colors?.background || DEFAULT_THEME.colors.background,
                    paper: nextTheme.colors?.paperBackground || DEFAULT_THEME.colors.background,
                },
                text: {
                    primary: nextTheme.colors?.text || DEFAULT_THEME.colors.text,
                },
                fallback: {
                    highlightLight: "#c8c1c2",
                },
            },
            shape: {
                borderRadius: borderRadius ? parseInt(borderRadius) : undefined,
            },
            typography: {
                fontFamily,
                fontSize: 16,
                htmlFontSize: 16,
                h5: undefined,
                h6: undefined,
                subtitle1: undefined,
                subtitle2: undefined,
                caption: undefined,
                overline: undefined,
                button: undefined,
                h1: {
                    ...DEFAULT_THEME.font?.h1,
                    ...nextTheme.font?.h1,
                    opacity: nextTheme.opacity?.primary,
                },
                h2: {
                    ...DEFAULT_THEME.font?.h2,
                    ...nextTheme.font?.h2,
                    opacity: nextTheme.opacity?.primary,
                },
                h3: {
                    ...DEFAULT_THEME.font?.h3,
                    ...nextTheme.font?.h3,
                    opacity: nextTheme.opacity?.primary,
                },
                h4: {
                    ...DEFAULT_THEME.font?.h4,
                    ...nextTheme.font?.h4,
                    opacity: nextTheme.opacity?.primary,
                },
                body1: {
                    ...DEFAULT_THEME.font?.body1,
                    ...nextTheme.font?.body1,
                    opacity: nextTheme.opacity?.primary,
                },
                body2: {
                    ...DEFAULT_THEME.font?.body2,
                    ...nextTheme.font?.body2,
                    opacity: nextTheme.opacity?.primary,
                },
                body3: {
                    ...DEFAULT_THEME.font?.body3,
                    ...nextTheme.font?.body3,
                    opacity: nextTheme.opacity?.primary,
                },
                label: {
                    ...DEFAULT_THEME.font?.label,
                    ...nextTheme.font?.label,
                    opacity: nextTheme.opacity?.primary,
                },
            },
            breakpoints: {
                values: {
                    xs: 0,
                    sm: 640,
                    md: 1024,
                    lg: 1280,
                    xl: 1680,
                    xxl: 1996,
                },
            },
            colors: nextTheme.colors,
            general: nextTheme.general,
            layout: nextTheme.layout,
            login: nextTheme.login,
            opacity: nextTheme.opacity,
            pageNavigation: nextTheme.pageNavigation,
            radius: nextTheme.radius,
            margin: nextTheme.margin,
            box: nextTheme.box,
            font,
            animation: nextTheme.animation,
            overwrites: {
                components: {
                    partlist: {
                        groupHeader: {
                            // Use for all Catalogs the monochrom as default
                            monochrom: true,
                        },
                    },
                    button: {
                        primary: {
                            fontFamily: buttonFontFamily,
                        },
                    },
                    tabs: {
                        font: {
                            fontFamily: tabFontFamily,
                        },
                    },
                    basketSummary: {
                        component: {
                            backgroundColor: nextTheme.colors?.highlight,
                        },
                    },
                },
            },
            components: {
                MuiTextField: {
                    variants: [
                        {
                            props: { size: "extralarge" },
                            style: {},
                        },
                    ],
                },
                MuiButton: {
                    styleOverrides: {
                        root: {
                            lineHeight: "1.2",
                        },
                        sizeSmall: {
                            fontSize: "0.875rem",
                            padding: "0.3em",
                        },
                        sizeMedium: {
                            fontSize: "1rem",
                            padding: "6px",
                        },
                        sizeLarge: {
                            fontSize: "1.125rem",
                            padding: "0.5em",
                        },
                    },
                    variants: [
                        {
                            props: { variant: "bordered", size: "extralarge" },
                            style: {
                                [`&::before`]: {
                                    content: "''",
                                    display: "flex",
                                    width: "100%",
                                    height: "3px",
                                    position: "absolute",
                                    bottom: "0",
                                },
                            },
                        },
                    ],
                },
                MuiIconButton: {
                    styleOverrides: {
                        root: {
                            color: alpha(
                                nextTheme.colors.dark || DEFAULT_THEME.colors.dark,
                                nextTheme.opacity?.primary || DEFAULT_THEME.opacity.primary
                            ),
                        },
                    },
                },
                MuiInputBase: {
                    styleOverrides: {
                        sizeSmall: {
                            fontSize: "0.8rem",
                        },
                    },
                },
                MuiFilledInput: {
                    styleOverrides: {
                        input: ({ ownerState, theme }) => ({
                            [`&.${inputBaseClasses.sizeSmall}`]: {
                                fontSize: ownerState.size ? theme.overwrites?.components?.textfield?.fontSize?.small : "14px",
                                lineHeight: ownerState.size ? theme.overwrites?.components?.textfield?.fontSize?.small : "14px",
                            },
                            fontSize: ownerState.size ? theme.overwrites?.components?.textfield?.fontSize?.medium : "16px",
                            lineHeight: ownerState.size ? theme.overwrites?.components?.textfield?.fontSize?.medium : "16px",
                            // large has to be defined soon
                        }),
                        root: {
                            borderColor: colors.grey[400],
                            borderStyle: "solid",
                            borderWidth: 1,
                            background: colors.grey[50],
                        },
                    },
                },
                MuiSelect: {
                    styleOverrides: {
                        standard: {
                            padding: "0.346em 2em 0.346em 0.5em",
                        },
                        outlined: {
                            padding: "5px 2em 5px 0.5em",
                        },
                    },
                },
                MuiSvgIcon: {
                    styleOverrides: {
                        fontSizeSmall: {
                            fontSize: "16px",
                        },
                        fontSizeMedium: {
                            fontSize: "18px",
                        },
                        fontSizeLarge: {
                            fontSize: "24px",
                        },
                    },
                },
                MuiRadio: {
                    styleOverrides: {
                        root: {
                            [` .${svgIconClasses.root}`]: {
                                fontSize: "22px",
                            },
                        },
                    },
                },
            },
        },
        {
            overwrites: nextTheme.overwrites ?? {},
            components: nextTheme.components ?? {},
        }
    )
}
